import { useEffect } from 'react';

import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { isNonEmptyString } from '@/utils/string';

import ResponseMessage from '../Error/ResponseMessage';
import Icon from '../Icon/Icon';
import { HStack, VStack } from '../Layout';
import { Text } from '../Typography/Text';
import { Button } from '../ui/button';

import type { BaseProps } from './types';

export const DropZoneImage = ({
  getRootProps,
  getInputProps,
  isDragActive,
  acceptedFiles,
  onResetButtonClick,
  uploadCompleted,
  uploadError,
  initialFile,
  onDeleteInitialFile,
}: BaseProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.DropZone',
  });

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      const objectUrl = URL.createObjectURL(acceptedFiles[0]);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [acceptedFiles]);

  const getFileNameFromUrl = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      return pathname.split('/').pop() || '';
    } catch (_) {
      console.error('Invalid URL');
      return '';
    }
  };

  const onInitialFileResetButtonClick = () => {
    onDeleteInitialFile?.();
  };

  const onDownloadButtonClick = () => {
    if (initialFile?.imgSrc) {
      const link = document.createElement('a');
      link.href = initialFile.imgSrc;
      link.download =
        initialFile.fileName || getFileNameFromUrl(initialFile.imgSrc);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      {initialFile && isNonEmptyString(initialFile.imgSrc) ? (
        <>
          <HStack className="gap-2 items-center">
            <div className="rounded-md shadow-inner bg-primary-muted-fill p-[6px]">
              <img
                src={initialFile.imgSrc}
                alt="uploaded file preview"
                className="object-cover w-6 h-6"
              />
            </div>
            <HStack className="gap-1 justify-between items-center">
              <Text className="text-xs text-neutral-muted-on-fill">
                {initialFile.fileName || getFileNameFromUrl(initialFile.imgSrc)}
              </Text>
              {initialFile?.editMode ? (
                <Button
                  variant="ghost"
                  className="p-2 w-8 h-8"
                  onClick={onInitialFileResetButtonClick}
                >
                  <Icon iconName="Trash2Icon" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="p-2 w-8 h-8"
                  onClick={onDownloadButtonClick}
                >
                  <Icon iconName="Download" />
                </Button>
              )}
            </HStack>
          </HStack>
        </>
      ) : (
        <>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <HStack className="gap-2 items-center">
              <div
                className={cn(
                  'rounded-md shadow-inner p-[6px]',
                  isDragActive ? 'bg-neutral-200' : 'bg-primary-muted-fill',
                )}
              >
                {!acceptedFiles.length && (
                  <Icon
                    iconName="ImageUpIcon"
                    size={24}
                    colorVariant="primary"
                  />
                )}
                {acceptedFiles.length > 0 && !uploadCompleted && (
                  <Icon
                    iconName="LoaderCircleIcon"
                    size={24}
                    colorVariant="primary"
                    className="animate-spin"
                  />
                )}
                {acceptedFiles.length > 0 && uploadCompleted && (
                  <img
                    src={URL.createObjectURL(acceptedFiles[0])}
                    alt="uploaded file preview"
                    className="object-cover w-6 h-6"
                  />
                )}
              </div>
              {acceptedFiles.length > 0 && uploadCompleted ? (
                <HStack className="gap-1 justify-between items-center">
                  <Text className="text-xs text-neutral-muted-on-fill">
                    {acceptedFiles[0].name}
                  </Text>
                  <Button
                    variant="ghost"
                    className="p-2 w-8 h-8"
                    onClick={onResetButtonClick}
                  >
                    <Icon iconName="Trash2Icon" />
                  </Button>
                </HStack>
              ) : (
                <VStack>
                  <Text className="text-xs font-semibold text-neutral-muted-on-fill">
                    {t('image.dropZoneTitle')}
                  </Text>
                  <Text className="text-xs text-neutral-muted-on-fill">
                    {t('image.imageFileOnly')}
                  </Text>
                </VStack>
              )}
            </HStack>
          </div>
          {uploadError && (
            <ResponseMessage className="whitespace-pre-line">
              {uploadError}
            </ResponseMessage>
          )}
        </>
      )}
    </>
  );
};
